import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import {
  Check,
  Eye,
  EyeOff,
  Laptop,
  Link2,
  LoaderCircle,
  Mail,
  Moon,
  RefreshCw,
  Smartphone,
  Sun,
} from "lucide-react";
import { Button, Field, IconButton, Input } from "@/components/ui";
import { ROUTES } from "@/constants/routes";
import { unlockRingtoneAudio } from "@/features/phone";
import { cn } from "@/lib/cn";
import { useAuthStore, useUiStore } from "@/store";

const QR_REFRESH_MS = 45_000;

type LoginMode = "qr" | "email";

function createSessionToken() {
  const random =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  return `papaya-link:${random}`;
}

export function LoginPage() {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const loginWithEmail = useAuthStore((state) => state.loginWithEmail);
  const theme = useUiStore((state) => state.theme);
  const toggleTheme = useUiStore((state) => state.toggleTheme);

  const [mode, setMode] = useState<LoginMode>("qr");
  const [sessionToken, setSessionToken] = useState(() => createSessionToken());
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrError, setQrError] = useState<string | null>(null);
  const [keepSignedIn, setKeepSignedIn] = useState(true);
  const [status, setStatus] = useState<"ready" | "linking" | "error">("ready");
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(QR_REFRESH_MS / 1000);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (mode !== "qr") return;

    let cancelled = false;

    const renderQr = async () => {
      try {
        const payload = JSON.stringify({
          v: 1,
          app: "papaya-web",
          token: sessionToken,
          issuedAt: Date.now(),
        });
        const url = await QRCode.toDataURL(payload, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 280,
          color: {
            dark: "#2a1a12",
            light: "#ffffff",
          },
        });
        if (!cancelled) {
          setQrDataUrl(url);
          setQrError(null);
        }
      } catch {
        if (!cancelled) {
          setQrDataUrl(null);
          setQrError("Could not generate QR code.");
        }
      }
    };

    void renderQr();
    return () => {
      cancelled = true;
    };
  }, [mode, sessionToken]);

  useEffect(() => {
    if (mode !== "qr") return;

    setSecondsLeft(QR_REFRESH_MS / 1000);
    const tick = window.setInterval(() => {
      setSecondsLeft((value) => (value <= 1 ? QR_REFRESH_MS / 1000 : value - 1));
    }, 1000);
    const refresh = window.setInterval(() => {
      setSessionToken(createSessionToken());
      setError(null);
      setStatus("ready");
    }, QR_REFRESH_MS);

    return () => {
      window.clearInterval(tick);
      window.clearInterval(refresh);
    };
  }, [mode, sessionToken]);

  if (isAuthenticated) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }

  const switchMode = (next: LoginMode) => {
    setMode(next);
    setError(null);
    setStatus("ready");
  };

  const refreshQr = () => {
    setSessionToken(createSessionToken());
    setError(null);
    setStatus("ready");
  };

  const submitEmailLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    unlockRingtoneAudio();
    setError(null);
    setStatus("linking");
    const result = await loginWithEmail(email, password);
    if (!result.ok) {
      setStatus("error");
      setError(result.error);
      return;
    }
    navigate(ROUTES.dashboard, { replace: true });
  };

  return (
    <div className="relative flex min-h-svh items-center justify-center overflow-hidden bg-bg px-4 py-10">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 12% 18%, rgba(251,170,51,0.2), transparent 32%), radial-gradient(circle at 88% 82%, rgba(114,176,85,0.16), transparent 34%)",
        }}
      />

      <button
        type="button"
        onClick={toggleTheme}
        className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-fg-muted shadow-sm hover:bg-surface-muted"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </button>

      <div className="relative w-full max-w-4xl overflow-hidden rounded-[12px] border border-border bg-surface shadow-[0_24px_70px_rgba(65,33,10,0.12)] dark:shadow-[0_24px_70px_rgba(0,0,0,0.45)]">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
          {/* Steps panel */}
          <div className="border-b border-border bg-surface-muted px-6 py-8 sm:px-10 lg:border-r lg:border-b-0">
            <div className="mb-8 flex items-center gap-3">
              <img src="/logo.png" alt="Papaya" className="h-11 w-11 object-contain" />
              <div>
                <p className="text-lg font-semibold tracking-tight text-fg">Papaya Web</p>
                <p className="text-sm text-fg-muted">
                  {mode === "qr" ? "Link with your phone" : "Sign in with email"}
                </p>
              </div>
            </div>

            {mode === "qr" ? (
              <>
                <h1 className="text-2xl font-semibold tracking-tight text-fg sm:text-[28px]">
                  Use Papaya on your computer
                </h1>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-fg-muted">
                  Scan this QR code with the Papaya mobile app to sign in — same flow as WhatsApp
                  Web.
                </p>

                <ol className="mt-8 space-y-4">
                  <Step
                    index={1}
                    icon={<Smartphone className="h-4 w-4" />}
                    title="Open Papaya on your phone"
                  />
                  <Step
                    index={2}
                    icon={<Laptop className="h-4 w-4" />}
                    title="Tap Menu or Settings, then Linked devices"
                  />
                  <Step
                    index={3}
                    icon={<Link2 className="h-4 w-4" />}
                    title="Tap Link a device and point your phone at this screen"
                  />
                </ol>
              </>
            ) : (
              <>
                <h1 className="text-2xl font-semibold tracking-tight text-fg sm:text-[28px]">
                  Sign in with email
                </h1>
                <p className="mt-2 max-w-md text-sm leading-relaxed text-fg-muted">
                  Use your Papaya account email and password to access the web dashboard.
                </p>

                <ol className="mt-8 space-y-4">
                  <Step
                    index={1}
                    icon={<Mail className="h-4 w-4" />}
                    title="Enter the email for your Papaya account"
                  />
                  <Step
                    index={2}
                    icon={<Check className="h-4 w-4" />}
                    title="Enter your password and continue"
                  />
                </ol>
              </>
            )}

            <label className="mt-10 flex cursor-pointer items-center gap-2.5 text-sm text-fg-muted">
              <input
                type="checkbox"
                checked={keepSignedIn}
                onChange={(event) => setKeepSignedIn(event.target.checked)}
                className="h-4 w-4 rounded border-border accent-papaya-deep"
              />
              Keep me signed in on this browser
            </label>
          </div>

          {/* Auth panel */}
          <div className="flex flex-col items-center justify-center px-6 py-10 sm:px-10">
            {mode === "qr" ? (
              <>
                <div className="relative">
                  <div
                    className={cn(
                      "relative flex h-[280px] w-[280px] items-center justify-center rounded-[12px] border border-border bg-white p-3 shadow-[0_8px_24px_rgba(42,26,18,0.08)]",
                      status === "linking" && "opacity-60",
                    )}
                  >
                    {qrDataUrl ? (
                      <img
                        src={qrDataUrl}
                        alt="Papaya login QR code"
                        className="h-full w-full rounded-[8px] object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-fg-muted">
                        <LoaderCircle className="h-6 w-6 animate-spin" />
                        <span className="text-xs">{qrError ?? "Preparing QR…"}</span>
                      </div>
                    )}

                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-white shadow-sm ring-1 ring-border">
                        <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
                      </span>
                    </div>
                  </div>

                  {status === "linking" ? (
                    <div className="absolute inset-0 flex items-center justify-center rounded-[12px] bg-surface/70 backdrop-blur-[2px]">
                      <div className="flex items-center gap-2 rounded-full bg-surface px-3 py-2 text-sm font-medium text-fg shadow-sm ring-1 ring-border">
                        <LoaderCircle className="h-4 w-4 animate-spin text-papaya-deep" />
                        Linking device…
                      </div>
                    </div>
                  ) : null}
                </div>

                <p className="mt-5 text-center text-sm text-fg-muted">
                  QR refreshes in <span className="font-semibold text-fg">{secondsLeft}s</span>
                </p>

                {error ? (
                  <p className="mt-3 text-center text-sm text-danger" role="alert">
                    {error}
                  </p>
                ) : null}

                <div className="mt-6 flex w-full max-w-[280px] flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={refreshQr}
                    className="inline-flex items-center justify-center gap-2 rounded-[12px] px-3 py-2.5 text-sm font-medium text-fg-muted hover:bg-surface-muted hover:text-fg"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Refresh QR code
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => switchMode("email")}
                  className="mt-5 text-sm font-medium text-papaya-deep hover:underline"
                >
                  Log in with email instead
                </button>
              </>
            ) : (
              <form
                onSubmit={(event) => void submitEmailLogin(event)}
                className="w-full max-w-[320px] space-y-4"
              >
                <Field label="Email" htmlFor="login-email">
                  <Input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={status === "linking"}
                    required
                  />
                </Field>

                <Field label="Password" htmlFor="login-password">
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      disabled={status === "linking"}
                      className="pr-10"
                      required
                    />
                    <IconButton
                      label={showPassword ? "Hide password" : "Show password"}
                      onClick={() => setShowPassword((value) => !value)}
                      disabled={status === "linking"}
                      className="absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </IconButton>
                  </div>
                </Field>

                {error ? (
                  <p className="text-sm text-danger" role="alert">
                    {error}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  className="w-full"
                  loading={status === "linking"}
                  disabled={status === "linking"}
                >
                  Sign in
                </Button>

                <button
                  type="button"
                  onClick={() => switchMode("qr")}
                  className="w-full text-center text-sm font-medium text-papaya-deep hover:underline"
                >
                  Log in with QR code instead
                </button>

              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Step({
  index,
  icon,
  title,
}: {
  index: number;
  icon: ReactNode;
  title: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-papaya-deep text-xs font-semibold text-white">
        {index}
      </span>
      <div className="pt-0.5">
        <p className="flex items-center gap-2 text-sm font-medium text-fg">
          <span className="text-fg-subtle">{icon}</span>
          {title}
        </p>
      </div>
    </li>
  );
}
